import { Delimiter, For, PlaceablePopover, Show } from '@library/components'
import cn from 'classnames'
import type { FC, RefObject } from 'react'
import { useEffect, useMemo, useState } from 'react'
import { TransitionGroup } from 'react-transition-group'

import { isDefined } from '../../guards'
import { groupBy } from '../../utils'
import { useModalManager } from '../ModalManager'
import css from './ActionsRender.css'
import { useActionContext } from './context'
import { sortActions } from './helpers'
import type { IAction } from './interfaces'

type ActionRenderProps = {
  anchorRef: RefObject<HTMLElement>
  className?: string
}

export const ActionRender: FC<ActionRenderProps> = ({ anchorRef, className = '' }) => {
  const {
    objectActions,
    sceneActions,
    selectedObjectAction,
    objects,
    editAction,
    selectedSceneAction,
    isLocked,
  } = useActionContext()
  const { other } = useModalManager()

  const [objectActionGroups, setObjectActionGroups] = useState<IAction<unknown>[][]>([])

  useEffect(() => {
    if (isLocked) return

    const canBeApplied = objectActions.filter(action => action.canApply(objects))
    const toShow = selectedObjectAction ? [selectedObjectAction] : canBeApplied

    setObjectActionGroups(
      Object.values(groupBy(toShow, action => action.group))
        .filter(isDefined)
        .map(sortActions),
    )
  }, [isLocked, objectActions, objects, selectedObjectAction])

  const sceneActionsToShow = useMemo(() => {
    const canBeApplied = sceneActions.filter(action => action.canApply(objects))
    return selectedSceneAction ? [selectedSceneAction] : canBeApplied
  }, [sceneActions, objects, selectedSceneAction])

  const hasObjectActions = objectActionGroups.length > 0
  const showObjectActions = (hasObjectActions && !selectedSceneAction) || !!editAction || isLocked
  const showSceneActions =
    !selectedObjectAction && !editAction && sceneActionsToShow.length > 0 && !isLocked

  return (
    <PlaceablePopover
      className={css.container}
      anchorRef={anchorRef}
      container={other.current ?? undefined}
      offset="s"
      direction="downCenter"
      spareDirection="downCenter">
      <Show when={showObjectActions}>
        <div
          className={cn(
            css.actionsContainer,
            { [css.selected]: isDefined(selectedObjectAction) },
            className,
          )}>
          <TransitionGroup component={null}>
            {editAction ? (
              <Show when={!!editAction}>{editAction.render()}</Show>
            ) : (
              <For each={objectActionGroups}>
                {(group, index) => (
                  <>
                    <For each={group}>{action => action.render()}</For>
                    <Show when={objectActionGroups.length - 1 !== index}>
                      <Delimiter />
                    </Show>
                  </>
                )}
              </For>
            )}
          </TransitionGroup>
        </div>
      </Show>
      <Show when={showSceneActions}>
        <div
          className={cn(
            css.actionsContainer,
            { [css.selected]: isDefined(selectedSceneAction) },
            className,
          )}>
          <TransitionGroup component={null}>
            <For each={sceneActionsToShow}>{action => action.render()}</For>
          </TransitionGroup>
        </div>
      </Show>
    </PlaceablePopover>
  )
}
